// src/v2/pulse/PortfolioCommandPage.jsx
//
// PORTFOLIO-COMMAND-IA: Shell page for Portfolio Command with:
//   - Underline pillar tab switcher (Data Trust · Plan Coverage · Review Cadence)
//   - Content panel for the active tab
//
// DataTrustDrilldown is rendered as a content panel (no breadcrumbs).
// Plan Coverage stays coming soon until activated by the command-index.
// Review Cadence shows live weekly meetings and their configured contribution.

import { useEffect, useState } from "react";
import { T } from "../../app/tokens.js";
import { DataTrustDrilldown } from "./DataTrustDrilldown.jsx";
import { getCommandIndex } from "../../data/commandIndexClient.js";
import { WeeklyReviewMeetings } from "./WeeklyReviewMeetings.jsx";
import { isCommandPillarLive } from "./commandPillars.js";
import { subscribeCommandIndexStale, unsubscribeCommandIndexStale } from "../../services/cacheBus.js";

// ─── Tab definitions ──────────────────────────────────────────────────────────

const TABS = [
  { key: "datatrust",      label: "Data Trust",     viewParam: "datatrust" },
  { key: "plan-coverage",  label: "Plan Coverage",  viewParam: "plan-coverage" },
  { key: "review-cadence", label: "Review Cadence", viewParam: "review-cadence" },
];

// ─── Sub-components ───────────────────────────────────────────────────────────

function PlanCoverageDrilldown({ pillar, onNavigate }) {
  const breakdown = pillar?.breakdown ?? null;
  const score     = pillar?.score ?? null;
  const covered   = breakdown?.covered ?? null;
  const total     = breakdown?.total   ?? null;

  return (
    <div
      data-testid="plan-coverage-drilldown"
      style={{
        background:   "var(--v2-card)",
        border:       "1px solid var(--v2-line)",
        borderRadius: 16,
        boxShadow:    "var(--v2-shadow-card)",
        padding:      "28px 28px 24px",
      }}
    >
      <div style={{ marginBottom: 18 }}>
        <div style={{ fontSize: T.fsMd, fontWeight: T.fwBold, color: "var(--v2-dark)", marginBottom: 4 }}>
          Plan Coverage
        </div>
        <div style={{ fontSize: T.fsSm, color: "var(--v2-mid)" }}>
          {covered !== null && total !== null
            ? `${covered} of ${total} markets have a plan set (a disposition + target date)`
            : "Markets that have a plan set (a disposition + target date)."}
        </div>
      </div>

      {/* Score summary */}
      <div
        data-testid="plan-coverage-summary"
        style={{
          display:       "flex",
          alignItems:    "center",
          gap:           20,
          padding:       "16px 20px",
          background:    "var(--v2-tint)",
          borderRadius:  10,
          marginBottom:  20,
        }}
      >
        <span style={{ fontSize: T.fsXl6, fontWeight: T.fwBold, color: "var(--v2-accent)" }}>
          {score !== null ? `${score}` : "—"}
        </span>
        <div>
          <div style={{ fontSize: T.fsMd, fontWeight: T.fwBold, color: "var(--v2-dark)" }}>
            {covered !== null && total !== null ? `${covered} of ${total} markets covered` : "Loading…"}
          </div>
          <div style={{ fontSize: T.fsSm5, color: "var(--v2-mid)", marginTop: 2 }}>
            {covered !== null && total !== null
              ? `${covered} of ${total} markets have a plan set (a disposition + target date)`
              : "Markets that have a plan set (a disposition + target date)."}
          </div>
        </div>
      </div>

      {/* CTA */}
      <button
        data-testid="plan-coverage-goto-plan"
        type="button"
        onClick={() => onNavigate?.("?view=plan")}
        style={{
          background:     "none",
          border:         "1px solid var(--v2-accent)",
          borderRadius:   8,
          padding:        "8px 16px",
          fontSize:       T.fsSm5,
          fontWeight:     T.fwSemibold,
          color:          "var(--v2-accent)",
          cursor:         "pointer",
        }}
      >
        Go to Market Plan →
      </button>
    </div>
  );
}

// ─── PortfolioCommandPage ─────────────────────────────────────────────────────

/**
 * PortfolioCommandPage
 *
 * Container rendered by PulsePage for view values:
 *   datatrust | plan-coverage | review-cadence | portfolio-command
 *
 * @param {string}   activeTab       - "datatrust" | "plan-coverage" | "review-cadence"
 * @param {Function} onNavigate      - Called with a ?view= URL string to navigate.
 * @param {string}   [userRole]      - "viewer" | "editor" | "admin"
 * @param {Function} [onOpenLinkedRun]
 */
export function PortfolioCommandPage({
  activeTab = "datatrust",
  onNavigate,
  userRole = "viewer",
  onOpenLinkedRun,
  planEnabled = true,
  reviewsEnabled = true,
}) {
  const [indexData, setIndexData] = useState(null);
  const [indexLoading, setIndexLoading] = useState(true);
  const [indexError, setIndexError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getCommandIndex()
      .then((result) => {
        if (!cancelled) {
          setIndexData(result ?? null);
          setIndexError(!result);
        }
      })
      .catch(() => { if (!cancelled) setIndexError(true); })
      .finally(() => { if (!cancelled) setIndexLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // Re-fetch command index when a plan save may have changed Plan Coverage.
  useEffect(() => {
    function handleStale() {
      getCommandIndex()
        .then((result) => { if (result) setIndexData(result); })
        .catch(() => {});
    }
    subscribeCommandIndexStale(handleStale);
    return () => { unsubscribeCommandIndexStale(handleStale); };
  }, []);

  const pillars = indexData?.pillars ?? [];
  const planPillar = pillars.find((pillar) => pillar.key === "plan_coverage") ?? null;
  const reviewPillar = pillars.find((pillar) => pillar.key === "review_cadence") ?? null;
  const tabs = TABS.filter((tab) => (
    (tab.key !== "plan-coverage" || planEnabled)
  )).map((tab) => ({
    ...tab,
    // Plan remains an explicit coming-soon tab until the server activates it.
    comingSoon: tab.key === "plan-coverage"
      && !isCommandPillarLive({ key: "plan_coverage" }, indexData),
  }));

  const handleTabClick = (tab) => {
    if (tab.comingSoon) return;
    onNavigate?.(`?view=${tab.viewParam}`);
  };

  return (
    <div data-testid="portfolio-command-page">
      {/* Underline pillar tabs */}
      <div
        data-testid="portfolio-command-tabs"
        style={{
          display:      "flex",
          alignItems:   "flex-end",
          gap:          0,
          borderBottom: "2px solid var(--v2-line)",
          marginBottom: 24,
          marginTop:    20,
        }}
      >
        {tabs.map((tab) => {
          const isActive     = activeTab === tab.key;
          const isComingSoon = !!tab.comingSoon;
          return (
            <button
              key={tab.key}
              data-testid={`portfolio-command-tab-${tab.key}`}
              type="button"
              onClick={() => handleTabClick(tab)}
              disabled={isComingSoon}
              style={{
                background:     "none",
                border:         "none",
                borderBottom:   isActive ? "2px solid var(--v2-accent)" : "2px solid transparent",
                marginBottom:   -2,
                padding:        "10px 18px",
                fontSize:       T.fsSm5,
                fontWeight:     isActive ? T.fwBold : T.fwSemibold,
                color:          isComingSoon
                  ? "var(--v2-light)"
                  : isActive
                    ? "var(--v2-accent)"
                    : "var(--v2-mid)",
                cursor:         isComingSoon ? "default" : "pointer",
                whiteSpace:     "nowrap",
                transition:     "color .15s",
                opacity:        isComingSoon ? 0.5 : 1,
                display:        "inline-flex",
                alignItems:     "center",
                gap:            6,
              }}
            >
              {tab.label}
              {isComingSoon && (
                <span
                  style={{
                    fontSize:     T.fsXxs5,
                    fontWeight:   T.fwSemibold,
                    color:        "var(--v2-light)",
                    background:   "var(--v2-hair)",
                    borderRadius: 999,
                    padding:      "1px 6px",
                  }}
                >
                  soon
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      {activeTab === "datatrust" && tabs.some((tab) => tab.key === "datatrust") && (
        <DataTrustDrilldown
          commandIndex={indexData}
          onNavigate={onNavigate}
          userRole={userRole}
          onOpenLinkedRun={onOpenLinkedRun}
        />
      )}

      {activeTab === "plan-coverage" && tabs.some((tab) => tab.key === "plan-coverage") && (
        isCommandPillarLive({ key: "plan_coverage" }, indexData) ? (
          <PlanCoverageDrilldown
            pillar={planPillar}
            onNavigate={onNavigate}
          />
        ) : (
          <div data-testid="plan-coverage-coming-soon" style={{
            padding: 28, background: "var(--v2-card)", border: "1px solid var(--v2-line)",
            borderRadius: 16, color: "var(--v2-mid)",
          }}>
            {indexLoading ? "Loading Plan Coverage…" : indexError
              ? "Plan Coverage status unavailable." : "Plan Coverage is coming soon."}
          </div>
        )
      )}

      {activeTab === "review-cadence" && tabs.some((tab) => tab.key === "review-cadence") && (
        <WeeklyReviewMeetings userRole={userRole} pillar={reviewPillar} />
      )}
    </div>
  );
}
